import { findKey } from 'lodash';

import { determineIndefiniteArticle } from 'src/lib/string';

import {
  HIDDEN_LANGUAGE,
  INFORMAL_LANGUAGE,
  LANGUAGE,
  type Language,
  LANGUAGE_TARGET_FORMALITY,
  type LanguageTargetFormality,
} from './const';

const LANGUAGES: Language[] = Object.values(LANGUAGE);
const HIDDEN_LANGUAGES: Language[] = Object.values(HIDDEN_LANGUAGE);

const LANGUAGE_LABELS = {
  [LANGUAGE.bg]: 'Bulgarian',
  [LANGUAGE.zh_tw]: 'Chinese (Traditional)',
  [LANGUAGE.zh_cn]: 'Chinese (Simplified)',
  [LANGUAGE.cs]: 'Czech',
  [LANGUAGE.da]: 'Danish',
  [LANGUAGE.de]: 'German (formal)',
  [LANGUAGE.de_in]: 'German (informal)',
  [LANGUAGE.de_ch]: 'Swiss German',
  [LANGUAGE.el]: 'Greek',
  [LANGUAGE.en_us]: 'English (US)',
  [LANGUAGE.en_gb]: 'English (UK)',
  [LANGUAGE.es]: 'Spanish',
  [LANGUAGE.es_cs]: 'Spanish (Castilian)',
  [LANGUAGE.es_la]: 'Spanish (Latin America)',
  [LANGUAGE.fr_qu]: 'French (Quebecois)',
  [LANGUAGE.fr]: 'French',
  [LANGUAGE.hu]: 'Hungarian',
  [LANGUAGE.it]: 'Italian',
  [HIDDEN_LANGUAGE.he]: 'Hebrew',
  [LANGUAGE.ja]: 'Japanese',
  [LANGUAGE.nl]: 'Dutch',
  [LANGUAGE.nl_be]: 'Dutch (Belgium)',
  [LANGUAGE.pl]: 'Polish',
  [LANGUAGE.pt_pt]: 'Portuguese',
  [LANGUAGE.pt_br]: 'Portuguese (BR)',
  [LANGUAGE.ro]: 'Romanian',
  [LANGUAGE.ru]: 'Russian',
  [LANGUAGE.cmn]: 'Chinese (Mandarin)',
  [LANGUAGE.yue]: 'Chinese (Cantonese)',
  [LANGUAGE.sv]: 'Swedish',
  [LANGUAGE.th]: 'Thai',
  [LANGUAGE.ms]: 'Malay',
  [LANGUAGE.ko]: 'Korean',
  [LANGUAGE.tr]: 'Turkish',
};

const LANGUAGE_LABELS_WITH_EMOJIS = {
  [LANGUAGE.bg]: '🇧🇬 Bulgarian',
  [LANGUAGE.cs]: '🇨🇿 Czech',
  [LANGUAGE.zh_cn]: '🇨🇳 Chinese (Simplified)',
  [LANGUAGE.zh_tw]: '🇨🇳 Chinese (Traditional)',
  [LANGUAGE.th]: '🇹🇭 Thai',
  [LANGUAGE.ms]: '🇲🇾 Malay',
  [LANGUAGE.da]: '🇩🇰 Danish',
  [LANGUAGE.de]: '🇩🇪 German (formal)',
  [LANGUAGE.de_in]: '🇩🇪 German (informal)',
  [LANGUAGE.de_ch]: '🇨🇭 Swiss German',
  [LANGUAGE.el]: '🇬🇷 Greek',
  [LANGUAGE.en_us]: '🇺🇸 English',
  [LANGUAGE.en_gb]: '🇬🇧 English UK',
  [LANGUAGE.es]: '🇪🇸 Spanish',
  [LANGUAGE.es_la]: '🇲🇽 Spanish (LA)',
  [LANGUAGE.es_cs]: '🇪🇸 Spanish (Castilian)',
  [LANGUAGE.fr]: '🇫🇷 French',
  [LANGUAGE.fr_qu]: '🇨🇦 French (Quebecois)',
  [LANGUAGE.hu]: '🇭🇺 Hungarian',
  [LANGUAGE.it]: '🇮🇹 Italian',
  [HIDDEN_LANGUAGE.he]: '🇮🇱 Hebrew',
  [LANGUAGE.ja]: '🇯🇵 Japanese',
  [LANGUAGE.nl]: '🇳🇱 Dutch',
  [LANGUAGE.nl_be]: '🇧🇪 Dutch (Belgium)',
  [LANGUAGE.pl]: '🇵🇱 Polish',
  [LANGUAGE.pt_pt]: '🇵🇹 Portuguese',
  [LANGUAGE.pt_br]: '🇧🇷 Portuguese (BR)',
  [LANGUAGE.ro]: '🇷🇴 Romanian',
  [LANGUAGE.ru]: '🇷🇺 Russian',
  [LANGUAGE.cmn]: '🇨🇳 Chinese (Mandarin)',
  [LANGUAGE.yue]: '🇨🇳 Chinese (Cantonese)',
  [LANGUAGE.sv]: '🇸🇪 Swedish',
  [LANGUAGE.ko]: '🇰🇷 Korean',
  [LANGUAGE.tr]: '🇹🇷 Turkish',
};

const LANGUAGE_CHIP_LABELS = {
  [LANGUAGE.bg]: 'BG',
  [LANGUAGE.cs]: 'CS',
  [LANGUAGE.zh_cn]: 'CH',
  [LANGUAGE.zh_tw]: 'CH',
  [LANGUAGE.da]: 'DA',
  [LANGUAGE.nl]: 'NL',
  [LANGUAGE.nl_be]: 'NL-BE',
  [LANGUAGE.en_us]: 'EN-US',
  [LANGUAGE.en_gb]: 'EN-UK',
  [LANGUAGE.fr]: 'FR',
  [LANGUAGE.fr_qu]: 'FR-QU',
  [LANGUAGE.de]: 'DE',
  [LANGUAGE.de_in]: 'DE',
  [LANGUAGE.de_ch]: 'DE-CH',
  [LANGUAGE.el]: 'EL',
  [HIDDEN_LANGUAGE.he]: 'HE',
  [LANGUAGE.hu]: 'HU',
  [LANGUAGE.it]: 'IT',
  [LANGUAGE.ja]: 'JA',
  [LANGUAGE.ms]: 'MS',
  [LANGUAGE.pl]: 'PL',
  [LANGUAGE.pt_pt]: 'PT',
  [LANGUAGE.pt_br]: 'PT',
  [LANGUAGE.ro]: 'RO',
  [LANGUAGE.ru]: 'RU',
  [LANGUAGE.es]: 'ES',
  [LANGUAGE.es_cs]: 'ES-CS',
  [LANGUAGE.es_la]: 'ES-LA',
  [LANGUAGE.cmn]: 'CMN',
  [LANGUAGE.sv]: 'SV',
  [LANGUAGE.th]: 'TH',
  [LANGUAGE.ko]: 'KO',
  [LANGUAGE.tr]: 'TR',
  [LANGUAGE.yue]: 'YUE',
};

export const AVAILABLE_LANGUAGES_FOR_BLOG: Language[] = [
  LANGUAGE.bg,
  LANGUAGE.cs,
  LANGUAGE.yue,
  LANGUAGE.cmn,
  LANGUAGE.zh_tw,
  LANGUAGE.zh_cn,
  LANGUAGE.da,
  LANGUAGE.nl,
  LANGUAGE.nl_be,
  LANGUAGE.en_us,
  LANGUAGE.en_gb,
  LANGUAGE.fr,
  LANGUAGE.fr_qu,
  LANGUAGE.de,
  LANGUAGE.el,
  HIDDEN_LANGUAGE.he,
  LANGUAGE.hu,
  LANGUAGE.it,
  LANGUAGE.ja,
  LANGUAGE.ko,
  LANGUAGE.ms,
  LANGUAGE.pl,
  LANGUAGE.pt_pt,
  LANGUAGE.pt_br,
  LANGUAGE.ro,
  LANGUAGE.ru,
  LANGUAGE.es,
  LANGUAGE.es_la,
  LANGUAGE.es_cs,
  LANGUAGE.sv,
  LANGUAGE.de_ch,
  LANGUAGE.th,
  LANGUAGE.tr,
];

export const getLanguageLabel = (language: Language): string => LANGUAGE_LABELS[language];

export const getLanguageLabelWithEmoji = (language: Language): string =>
  LANGUAGE_LABELS_WITH_EMOJIS[language];

export const getLanguageChipLabel = (language: Language): string => LANGUAGE_CHIP_LABELS[language];

const removeSuffix = (str: string, suffix: string): string | undefined => {
  if (!str) return;

  if (str.endsWith(suffix)) {
    return str.slice(0, -suffix.length);
  }
  return str;
};

const LANGUAGE_KEY_INFORMAL = '_in';
export const getLanguageWithoutFormality = (language: Language): string | undefined => {
  return removeSuffix(language, LANGUAGE_KEY_INFORMAL);
};

const LANGUAGE_SUFFIX_FORMAL = '(formal)';
export const getLanguageLabelWithoutFormal = (language: Language): string | undefined => {
  return removeSuffix(getLanguageLabel(language), LANGUAGE_SUFFIX_FORMAL);
};

export const getLanguageTargetFormality = (language: Language): string | undefined => {
  if (Object.keys(INFORMAL_LANGUAGE).includes(language)) {
    return LANGUAGE_TARGET_FORMALITY.informal;
  }
  if (Object.values(INFORMAL_LANGUAGE).includes(language)) {
    return LANGUAGE_TARGET_FORMALITY.formal;
  }
};

export const normalizeLanguage = (nonAligned: Language): Language => {
  return INFORMAL_LANGUAGE[nonAligned] || nonAligned;
};

const LANGUAGE_BACKEND_EN = 'en';
const LANGUAGE_BACKEND_PT = 'pt';
const LANGUAGE_ALIASES: Record<string, Language> = {
  [LANGUAGE_BACKEND_EN]: LANGUAGE.en_us,
  [LANGUAGE_BACKEND_PT]: LANGUAGE.pt_pt,
};

const getLanguageFromTargetFormality = (
  language: Language,
  languageTargetFormality: LanguageTargetFormality
): Language => {
  if (languageTargetFormality !== LANGUAGE_TARGET_FORMALITY.informal) {
    return language;
  }

  const informalLanguage = findKey(INFORMAL_LANGUAGE, (value) => language === value) as
    | Language
    | undefined;

  return informalLanguage ?? language;
};

export const alignLanguage = (
  nonAligned: Language,
  languageTargetFormality?: LanguageTargetFormality
): Language | undefined => {
  if (LANGUAGES.includes(nonAligned)) {
    return languageTargetFormality
      ? getLanguageFromTargetFormality(nonAligned, languageTargetFormality)
      : nonAligned;
  }

  if (HIDDEN_LANGUAGES.includes(nonAligned)) {
    return nonAligned;
  }

  if (LANGUAGE_ALIASES[nonAligned]) {
    return LANGUAGE_ALIASES[nonAligned];
  }

  return undefined;
};

export const isEnglishLanguage = (language?: Language): boolean => {
  if (!language) {
    return false;
  }

  return language.startsWith(LANGUAGE_BACKEND_EN);
};

export const isNonEnglish = (language?: Language): boolean => {
  // Treat "empty" as English
  if (!language) {
    return false;
  }

  return !isEnglishLanguage(language);
};

export const isSameLanguage = (lang1?: Language, lang2?: Language): boolean => {
  if (!lang1 || !lang2) {
    return false;
  }
  // Check if both are English variants
  if (!isNonEnglish(lang1) && !isNonEnglish(lang2)) {
    return true; // Consider all English variants as the same
  }

  const baseLang1 = getLanguageWithoutFormality(lang1);
  const baseLang2 = getLanguageWithoutFormality(lang2);
  return baseLang1 === baseLang2;
};

export const isHebrewLanguage = (language: Language): boolean => language === HIDDEN_LANGUAGE.he;

export const isRtlLanguage = (language: Language): boolean => isHebrewLanguage(language);

const NON_CAPITALIZED_LANGUAGES: Language[] = [
  HIDDEN_LANGUAGE.he,
  LANGUAGE.yue,
  LANGUAGE.cmn,
  LANGUAGE.ja,
];
export const isNonCapitalizedLanguage = (language: Language): boolean =>
  NON_CAPITALIZED_LANGUAGES.includes(language);

export const canCountCharactersForLanguage = (language: Language): boolean => {
  return isNonCapitalizedLanguage(language) && language !== HIDDEN_LANGUAGE.he;
};

export const brandVoiceLanguageErrorMessage = (language: Language, asset: string): string => {
  if (!language) {
    return '';
  }
  const alignedLanguage = alignLanguage(language);
  if (!alignedLanguage) {
    return '';
  }

  const languageLabel = getLanguageLabelWithoutFormal(alignedLanguage);
  const article = determineIndefiniteArticle(languageLabel);

  return `This ${asset} is being created for ${article} ${languageLabel} brand voice. Please make sure that this is written in ${languageLabel} as well.`;
};

export const sortByLanguage = (fieldName: string, language: Language) => {
  interface ItemWithLanguage {
    [fieldName]: Language;
  }

  return (a: ItemWithLanguage, b: ItemWithLanguage) => {
    if (isSameLanguage(a[fieldName], language) && !isSameLanguage(b[fieldName], language))
      return -1;
    if (!isSameLanguage(a[fieldName], language) && isSameLanguage(b[fieldName], language)) return 1;
    return 0;
  };
};
